import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, Router, NavigationEnd } from '@angular/router';
import { LanguageDropdownComponent } from '../language-dropdown/language-dropdown.component';
import { TranslocoModule } from '@jsverse/transloco';
import { SafeUrl } from '@angular/platform-browser';
import { LanguageRoutingService } from '../../services/language-routing.service';
import { ButtonComponent } from '../button/button.component';
import { AuthService } from '../../services/auth.service';
import { AvatarService } from '../../services/avatar.service';
import { UserDropdownComponent } from '../user-dropdown/user-dropdown.component';
import { EnvironmentService } from '../../../environments/environment-loader';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterLink, LanguageDropdownComponent, TranslocoModule, ButtonComponent, UserDropdownComponent],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  currentUrl: string = '/';
  mobileMenuOpen = false;
  readonly dashboardUrl: string;

  // Avatar shown next to the user name in the dropdown.
  firstName = '';
  lastName = '';
  userEmail = '';
  avatarPhoto: SafeUrl | string | null = null;
  private userSub?: Subscription;

  constructor(
    private readonly languageRoutingService: LanguageRoutingService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly avatarService: AvatarService,
    environmentService: EnvironmentService
  ) {
    this.dashboardUrl = environmentService.environment.dashboardUrl;
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.currentUrl = event.urlAfterRedirects;
      this.mobileMenuOpen = false; // Close mobile menu on navigation
    });
  }

  ngOnInit(): void {
    // React to login / avatar changes so the header avatar stays in sync.
    this.userSub = this.authService.currentUser$.subscribe((user) => {
      this.firstName = user?.firstName || '';
      this.lastName = user?.lastName || '';
      this.userEmail = user?.email || '';
      this.resolveAvatar(user?.photoThumbnail ? user : null, user?.photoID);
    });
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
  }

  private resolveAvatar(userWithThumbnail: { photoThumbnail?: string } | null, photoID?: number): void {
    const thumbnail = this.avatarService.getThumbnailDataUrl(userWithThumbnail as never);
    if (thumbnail) {
      this.avatarPhoto = thumbnail;
      return;
    }
    if (photoID) {
      this.avatarService.getAvatarUrl(photoID).subscribe({
        next: (url) => (this.avatarPhoto = url),
        error: () => (this.avatarPhoto = null),
      });
    } else {
      this.avatarPhoto = null;
    }
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
  }

  getLocalizedRoute(path: string): string {
    return this.languageRoutingService.getLocalizedRoute(path);
  }

  isLoggedIn(): boolean {
    return this.authService.isLoggedIn();
  }

  getUserName(): string {
    const currentUser = this.authService.currentUserValue;
    return currentUser?.firstName || '';
  }

  getUserEmail(): string {
    const currentUser = this.authService.currentUserValue;
    return currentUser?.email || '';
  }

  isRouteActive(path: string): boolean {
    // Remove language prefix if present
    const currentPath = this.currentUrl.replace(/^\/[a-z]{2}(\/|$)/, '/');
    // For root path, check if it's exactly the root or language root
    if (path === '/') {
      return currentPath === '/' || !!new RegExp(/^\/[a-z]{2}\/?$/).exec(this.currentUrl);
    }
    // For other paths, check if current URL starts with the path
    return currentPath.startsWith(path);
  }
}
